import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  deDE,
} from '@mui/x-data-grid';
import { Box, IconButton, Typography } from '@mui/material';
import { Add, Check, Clear } from '@mui/icons-material';
import { IMeetingEmailTemplate } from '../Interfaces/IMeetingEmailTemplate';
import { GenericMeetingTemplateEdit } from './GenericMeetingTemplateEdit';

export interface GenericOverviewProps<T extends IMeetingEmailTemplate> {
  title: string;
  rows: T[];
  getId: (item: T) => number;
  createEmpty: () => T;
  onSave: (item: T) => void;
  onDelete: (item: T) => void;
  headerActions?: React.ReactNode;
}

export function GenericMeetingEmailOverview<T extends IMeetingEmailTemplate>({
  title,
  rows,
  getId,
  createEmpty,
  onSave,
  onDelete,
  headerActions,
}: GenericOverviewProps<T>) {
  const [editItem, setEditItem] = useState<T | null>(null);

  const columns: GridColDef[] = [
    { field: 'Title', headerName: 'Bezeichnung', flex: 1 },
    {
      field: 'sendOnCreation',
      headerName: 'Terminerstellung',
      width: 150,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? <Check /> : <Clear />,
    },
    {
      field: 'sendOnReschedule',
      headerName: 'Terminänderung',
      width: 150,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? <Check /> : <Clear />,
    },
    {
      field: 'sendOnCancelling',
      headerName: 'Terminabsage',
      width: 150,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? <Check /> : <Clear />,
    },
    { field: 'DaysBeforeDate', headerName: '± Tage vor Termin', width: 150 },
    {
      field: 'sendWithCalenderFile',
      headerName: 'Kalenderdatei',
      width: 150,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? <Check /> : <Clear />,
    },
  ];

  return (
    <>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        mb={2}
      >
        <Typography variant='h4'>{title}</Typography>
        <Box display='flex' alignItems='center' gap={1}>
          {headerActions}
          <IconButton
            color='primary'
            onClick={() => setEditItem(createEmpty())}
          >
            <Add />
          </IconButton>
        </Box>
      </Box>

      <DataGrid
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        getRowId={getId}
        onRowClick={params => setEditItem(params.row as T)}
        autoHeight
      />

      {editItem && (
        <GenericMeetingTemplateEdit
          open={true}
          template={editItem}
          onSave={data => {
            onSave(data as T);
            setEditItem(null);
          }}
          onCancel={() => setEditItem(null)}
          onDelete={() => {
            onDelete(editItem);
            setEditItem(null);
          }}
        />
      )}
    </>
  );
}
